/**
 * 🆕 [Phase 30 #4] Récap annuel des BT internes pour un client interne.
 *
 * Affiché inline dans `FicheClient.jsx` uniquement quand `client.is_internal_client === true`.
 * Owner only — sinon la checkbox parent ne peut pas être visible.
 *
 * Fetch : GET /api/crm/clients/{client_id}/recap-bt-internes?annee=YYYY
 *  → { annee, annees_disponibles: [...], bts: [{ id, numero, titre, date,
 *      statut, lien, heures_reelles }], total_heures_reelles,
 *      total_cout_main_oeuvre, total_cout_materiel }
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Building2, Clock, DollarSign, Package, ChevronRight } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Link } from 'react-router-dom';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function formatMontant(v) {
  const n = Number(v || 0);
  return n.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 2 });
}

function formatHeures(v) {
  const n = Number(v || 0);
  return `${n.toFixed(1)} h`;
}

export function RecapBTInternesAnnuel({ clientId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [annee, setAnnee] = useState(new Date().getFullYear());

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios
      .get(`${API_URL}/api/crm/clients/${clientId}/recap-bt-internes`, {
        params: { annee },
      })
      .then((r) => {
        if (!cancelled) setData(r.data || null);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId, annee]);

  const anneesDispo = (data?.annees_disponibles || []).length
    ? data.annees_disponibles
    : [new Date().getFullYear(), new Date().getFullYear() - 1];

  const bts = data?.bts || [];
  const totalHeures = data?.total_heures_reelles ?? 0;
  const totalMo = data?.total_cout_main_oeuvre ?? 0;
  const totalMat = data?.total_cout_materiel ?? 0;

  return (
    <Card className="border-purple-200 bg-purple-50/40" data-testid="recap-bt-internes-card">
      <CardHeader className="pb-3 flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2 text-base text-purple-900">
          <Building2 className="w-5 h-5 text-purple-600" />
          Récap BT internes — Client interne
        </CardTitle>
        <Select value={String(annee)} onValueChange={(v) => setAnnee(Number(v))}>
          <SelectTrigger className="w-28 h-8 text-xs" data-testid="recap-bt-internes-year">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {anneesDispo.map((a) => (
              <SelectItem key={a} value={String(a)} data-testid={`recap-bt-internes-year-${a}`}>
                {a}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </CardHeader>

      <CardContent className="space-y-4">
        {loading ? (
          <p className="text-sm text-purple-600 italic">Chargement…</p>
        ) : bts.length === 0 ? (
          <p className="text-sm text-slate-500 italic" data-testid="recap-bt-internes-empty">
            Aucun BT interne enregistré pour {annee}.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat
                icon={<Building2 className="w-4 h-4" />}
                label="BTs internes"
                value={bts.length}
                testid="recap-bt-internes-count"
              />
              <Stat
                icon={<Clock className="w-4 h-4" />}
                label="Total heures"
                value={formatHeures(totalHeures)}
                testid="recap-bt-internes-heures"
              />
              <Stat
                icon={<DollarSign className="w-4 h-4" />}
                label="Coût main-d'œuvre"
                value={formatMontant(totalMo)}
                testid="recap-bt-internes-cout-mo"
              />
              <Stat
                icon={<Package className="w-4 h-4" />}
                label="Coût matériel"
                value={formatMontant(totalMat)}
                testid="recap-bt-internes-cout-materiel"
              />
            </div>

            <div className="border-t border-purple-200 pt-3">
              <p className="text-xs uppercase tracking-wide text-purple-700 font-semibold mb-2">
                Liste des BTs {annee}
              </p>
              <ul className="divide-y divide-purple-100 bg-white rounded-md border border-purple-100">
                {bts.slice(0, 25).map((bt) => (
                  <li key={bt.id} className="px-3 py-2 flex items-center gap-2 text-sm">
                    <Link
                      to={bt.lien || `/bons-travail/${bt.id}`}
                      className="flex-1 min-w-0 flex items-center gap-2 hover:text-purple-700"
                      data-testid={`recap-bt-internes-item-${bt.id}`}
                    >
                      <Badge variant="outline" className="font-mono text-[10px] border-purple-300 text-purple-700">
                        {bt.numero || 'BT'}
                      </Badge>
                      <span className="truncate font-medium">
                        {bt.titre || 'Travail interne'}
                      </span>
                      <ChevronRight className="w-3.5 h-3.5 text-purple-400 shrink-0" />
                    </Link>
                    <span className="text-xs text-slate-500 shrink-0">
                      {formatHeures(bt.heures_reelles)}
                    </span>
                  </li>
                ))}
                {bts.length > 25 && (
                  <li className="px-3 py-2 text-xs text-slate-500 italic">
                    + {bts.length - 25} autre(s)…
                  </li>
                )}
              </ul>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function Stat({ icon, label, value, testid }) {
  return (
    <div
      className="bg-white rounded-md p-3 border border-purple-100 flex flex-col gap-1"
      data-testid={testid}
    >
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-purple-600 font-semibold">
        {icon}
        {label}
      </span>
      <span className="text-lg font-bold text-slate-900">{value}</span>
    </div>
  );
}

export default RecapBTInternesAnnuel;
